import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageSquarePlus, X, Send, Bot, Sparkles, ChevronDown } from 'lucide-react';
import { TaskPriority, TaskStatus, TaskTeam, TaskUser } from '../types';
import { detectTeam } from '../utils/detectTeam';
import { apiService } from '../../../services/api';

interface ParsedTask {
  title: string;
  assigneeName: string;
  priority: TaskPriority;
  status: TaskStatus;
  team: TaskTeam;
  dueDate: string | null;
  description: string;
  labels: string[];
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  parsedTask?: ParsedTask;
}

interface ChatBoxProps {
  users: TaskUser[];
  currentUser: TaskUser;
  onCreateTask: (data: {
    title: string;
    assigneeId: number;
    priority: TaskPriority;
    status: TaskStatus;
    team: TaskTeam;
    dueDate: string | null;
    description: string;
    labels: string[];
  }) => void;
}

// ── Local fallback parser (used when backend is unreachable) ──────────────────

function generateBrief(title: string): string {
  const t = title.toLowerCase();
  if (t.includes('interview') || t.includes('hiring') || t.includes('recruit') || t.includes('candidate'))
    return 'Schedule and conduct structured interviews with all shortlisted candidates. Evaluate responses against the defined criteria and share feedback with the hiring panel. Finalise the decision with relevant stakeholders before sending out offer letters.';
  if (t.includes('p&l') || t.includes('report') || t.includes('finance') || t.includes('revenue'))
    return 'Compile the relevant financial data and cross-check figures across all locations. Ensure the numbers align with the source systems before sharing. Flag any discrepancies and provide a summary section at the top for quick executive review.';
  if (t.includes('bug') || t.includes('fix') || t.includes('error') || t.includes('issue'))
    return 'Reproduce the issue consistently and identify the root cause. Implement a targeted fix with minimal side effects. Test across all affected environments and edge cases before marking as resolved.';
  if (t.includes('payroll') || t.includes('salary') || t.includes('attendance') || t.includes('leave'))
    return 'Pull the latest data from the HR system and cross-check against attendance records. Identify any discrepancies and escalate to the relevant manager. Ensure the final figures are approved before processing.';
  if (t.includes('hr') || t.includes('employee') || t.includes('onboarding') || t.includes('offboarding'))
    return 'Coordinate with the HR team to complete the required steps and update all relevant records. Communicate next steps clearly to the employee and relevant managers. Confirm completion in the HR system once done.';
  if (t.includes('client') || t.includes('onboard') || t.includes('kam'))
    return 'Coordinate with the relevant city ops team to complete onboarding requirements. Verify that SKU mapping, billing configuration, and facility assignments are complete. Share a confirmation summary once done.';
  if (t.includes('invoice') || t.includes('vendor') || t.includes('approval') || t.includes('payment'))
    return 'Review the pending items in the queue and verify against supporting documents. Ensure amounts match POs or contracts. Approve or flag for clarification with a comment before the deadline.';
  if (t.includes('mail') || t.includes('email') || t.includes('reply') || t.includes('respond'))
    return 'Review all pending correspondence and draft clear, professional responses. Prioritise time-sensitive threads and loop in relevant team members where needed. Mark each thread as resolved once a reply has been sent.';
  if (t.includes('social') || t.includes('media') || t.includes('content') || t.includes('post'))
    return 'Plan and draft the required content pieces based on current goals and brand guidelines. Review with the relevant stakeholder before publishing. Track engagement metrics after posting.';
  if (t.includes('ui') || t.includes('interface') || t.includes('design') || t.includes('dashboard') || t.includes('board'))
    return 'Define the scope and component breakdown before starting. Build following existing design patterns and ensure responsive behaviour across screen sizes. Test across key user flows and get a design review before marking complete.';
  if (t.includes('build') || t.includes('feature') || t.includes('implement') || t.includes('develop') || t.includes('complete'))
    return 'Define the scope and acceptance criteria clearly before starting. Build the feature following existing patterns in the codebase. Test the happy path and edge cases, then get a peer review before marking complete.';
  return 'Break this task down into clear sub-steps before starting. Identify any dependencies or blockers upfront. Communicate progress at each stage and flag risks early so the team can course-correct if needed.';
}

function parseTaskLocally(input: string, users: TaskUser[]): ParsedTask {
  const lower = input.toLowerCase();

  // Priority
  let priority: TaskPriority = 'medium';
  if (lower.includes('urgent') || lower.includes('asap') || lower.includes('critical')) priority = 'urgent';
  else if (lower.includes('high priority') || lower.includes('very important')) priority = 'high';
  else if (lower.includes('low priority') || lower.includes('when possible') || lower.includes('not urgent')) priority = 'low';

  // Status
  let status: TaskStatus = 'todo';
  if (
    lower.includes('backlog') || lower.includes('deprioritize') ||
    lower.includes('not important') || lower.includes('put it aside') ||
    lower.includes('park it') || lower.includes('can wait') || lower.includes('not a priority')
  ) status = 'backlog';

  // Due date — full day-of-week support
  const today = new Date();
  function getNextDay(dayIndex: number): string {
    const diff = ((dayIndex - today.getDay() + 7) % 7) || 7;
    const d = new Date(today);
    d.setDate(today.getDate() + diff);
    return d.toISOString().split('T')[0];
  }

  let dueDate: string | null = null;
  if (lower.includes('today')) {
    dueDate = today.toISOString().split('T')[0];
  } else if (lower.includes('tomorrow')) {
    const d = new Date(today); d.setDate(d.getDate() + 1);
    dueDate = d.toISOString().split('T')[0];
  } else if (/\bsunday\b/.test(lower))    { dueDate = getNextDay(0); }
  else if (/\bmonday\b/.test(lower))      { dueDate = getNextDay(1); }
  else if (/\btuesday\b/.test(lower))     { dueDate = getNextDay(2); }
  else if (/\bwednesday\b/.test(lower))   { dueDate = getNextDay(3); }
  else if (/\bthursday\b/.test(lower))    { dueDate = getNextDay(4); }
  else if (/\bfriday\b/.test(lower) || lower.includes('end of week')) { dueDate = getNextDay(5); }
  else if (/\bsaturday\b/.test(lower))    { dueDate = getNextDay(6); }
  else if (lower.includes('next week'))   { const d = new Date(today); d.setDate(d.getDate() + 7); dueDate = d.toISOString().split('T')[0]; }
  else if (lower.includes('end of month') || lower.includes('month end')) {
    dueDate = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
  }

  // Assignee — only set if a specific person is mentioned; empty = unassigned
  const inputWords = lower.match(/\b[a-z]+\b/g) ?? [];
  let assigneeName = '';
  for (const user of users) {
    const firstName = user.name.trim().split(/\s+/)[0].toLowerCase();
    if (firstName.length < 3) continue;
    const isMatch = inputWords.some(word =>
      word.length >= 4 && (firstName.startsWith(word) || word.startsWith(firstName))
    );
    if (isMatch) { assigneeName = user.name; break; }
  }

  // Labels — detected first so team can use them as a reliable signal
  const labelSet = new Set<string>();
  if (lower.includes('bug') || lower.includes('fix') || lower.includes('error')) labelSet.add('Bug');
  if (lower.includes('feature') || lower.includes('build') || lower.includes('implement')) labelSet.add('Feature');
  if (lower.includes('finance') || lower.includes('p&l') || lower.includes('revenue') || lower.includes('invoice') || lower.includes('payment')) labelSet.add('Finance');
  if (lower.includes('frontend') || lower.includes('ui') || lower.includes('interface')) labelSet.add('Frontend');
  if (lower.includes('backend') || lower.includes('api') || lower.includes('server')) labelSet.add('Backend');
  if (lower.includes('ops') || lower.includes('operation') || lower.includes('shift') || lower.includes('mail') || lower.includes('email') || lower.includes('social') || lower.includes('media')) labelSet.add('Ops');
  if (/\bhr\b/.test(lower) || lower.includes('payroll') || lower.includes('attendance') ||
      lower.includes('interview') || lower.includes('hiring') || lower.includes('recruit') ||
      lower.includes('candidate')) labelSet.add('HR');
  if (lower.includes('kam') || lower.includes('client') || lower.includes('onboard')) labelSet.add('KAM');
  if (lower.includes('urgent') || lower.includes('asap')) labelSet.add('Urgent');
  const labels = [...labelSet];

  // Team — derive from labels first (labels are always correct), fall back to keyword scoring
  const team: TaskTeam = (() => {
    if (labelSet.has('HR')) return 'HR';
    if (labelSet.has('Finance')) return 'Finance';
    if (labelSet.has('KAM')) return 'KAM';
    if (labelSet.has('Ops') && !labelSet.has('Frontend') && !labelSet.has('Backend')) return 'Operations';
    if (labelSet.has('Frontend') || labelSet.has('Backend')) return 'Engineering';
    return detectTeam(input);
  })();

  // Title: strip common prefixes, then remove date/priority noise
  let title = input;

  // Pattern 1: "ask/tell/assign [name(s)] to [action]" → keep [action]
  const prefixMatch = title.match(/^(ask|tell|assign|get|have)\s+(.+?)\s+to\s+/i);
  if (prefixMatch) {
    title = title.slice(prefixMatch[0].length);
  } else {
    // Pattern 2: "ask/tell [name] [action]"
    title = title.replace(/^(ask|tell|assign|get|have)\s+\S+\s+/i, '');
    // Pattern 3: "the [subject] should/needs to/must/can/will [action]"
    const statementMatch = title.match(/^(?:the\s+)?(?:\S+\s+){1,3}(?:should|needs?\s+to|must|can|will|has?\s+to)\s+/i);
    if (statementMatch) title = title.slice(statementMatch[0].length);
    // Pattern 4: "I want/need to [action]"
    title = title.replace(/^i\s+(want|need|have)\s+to\s+/i, '');
  }

  // Remove date and priority noise
  title = title
    .replace(/\bby\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/gi, '')
    .replace(/\b(this|next)\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday|week)\b/gi, '')
    .replace(/\bend of (week|month)\b/gi, '')
    .replace(/\bmonth end\b/gi, '')
    .replace(/\b(today|tomorrow)\b/gi, '')
    .replace(/,?\s*(high|medium|low|urgent)\s*priority/gi, '')
    .replace(/\s*[—–-]\s*(high|medium|low|urgent)\s*priority/gi, '')
    .replace(/\b(urgent|asap|critical)\b/gi, '')
    .replace(/\s*[—–]\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!title) title = input.trim();
  title = title.charAt(0).toUpperCase() + title.slice(1);
  if (title.endsWith('.')) title = title.slice(0, -1);

  return { title, description: generateBrief(title), priority, status, team, dueDate, labels, assigneeName };
}

// ── API parser (with local fallback) ─────────────────────────────────────────

async function parseTaskFromAPI(input: string, users: TaskUser[]): Promise<ParsedTask> {
  try {
    const response = await apiService.post<{
      title: string; description: string; priority: TaskPriority; status: TaskStatus;
      team: string; due_date: string | null; labels: string[];
    }>('/tasks/ai/parse', { input });
    const d = response.data;

    // Match assignee — only set if a specific person is mentioned; empty = unassigned
    const lower = input.toLowerCase();
    const apiInputWords = lower.match(/\b[a-z]+\b/g) ?? [];
    let assigneeName = '';
    for (const user of users) {
      const firstName = user.name.trim().split(/\s+/)[0].toLowerCase();
      if (firstName.length < 3) continue;
      const isMatch = apiInputWords.some(word =>
        word.length >= 4 && (firstName.startsWith(word) || word.startsWith(firstName))
      );
      if (isMatch) { assigneeName = user.name; break; }
    }

    // Derive team from labels (overrides AI model's potentially wrong team field)
    const aiLabels: string[] = d.labels ?? [];
    const derivedTeam: TaskTeam = (() => {
      if (aiLabels.includes('HR')) return 'HR';
      if (aiLabels.includes('Finance')) return 'Finance';
      if (aiLabels.includes('KAM')) return 'KAM';
      if (aiLabels.includes('Ops') && !aiLabels.includes('Frontend') && !aiLabels.includes('Backend')) return 'Operations';
      if (aiLabels.includes('Frontend') || aiLabels.includes('Backend')) return 'Engineering';
      return (d.team as TaskTeam) ?? detectTeam(input);
    })();

    return {
      title: d.title,
      description: d.description,
      priority: d.priority,
      status: d.status ?? 'todo',
      team: derivedTeam,
      dueDate: d.due_date,
      labels: aiLabels,
      assigneeName,
    };
  } catch {
    // Backend unreachable — fall back to local parser
    return parseTaskLocally(input, users);
  }
}

export const ChatBox: React.FC<ChatBoxProps> = ({ users, currentUser, onCreateTask }) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      text: "Hi! I can create tasks from plain English. Try: \"Ask @Gouri to prepare the P&L report by Friday, high priority\"",
    },
  ]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const [mentionState, setMentionState] = useState<{ query: string; atIndex: number } | null>(null);

  const mentionUsers = mentionState
    ? users.filter(u => {
        const q = mentionState.query;
        return !q || u.name.toLowerCase().startsWith(q) || u.name.split(' ').some(w => w.toLowerCase().startsWith(q));
      }).slice(0, 5)
    : [];

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInput(val);
    const cursor = e.target.selectionStart ?? val.length;
    const before = val.slice(0, cursor);
    const match = before.match(/@(\w*)$/);
    setMentionState(match ? { query: match[1].toLowerCase(), atIndex: cursor - match[0].length } : null);
  }, []);

  const handleMentionSelect = useCallback((user: TaskUser) => {
    setMentionState(prev => {
      if (!prev) return null;
      const before = input.slice(0, prev.atIndex);
      const after = input.slice(prev.atIndex + 1 + prev.query.length);
      setInput(`${before}${user.name} ${after}`);
      return null;
    });
    chatInputRef.current?.focus();
  }, [input]);

  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userText = input.trim();
    setInput('');

    const userMsg: ChatMessage = { id: `u-${Date.now()}`, role: 'user', text: userText };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    let parsed: ParsedTask;
    try {
      parsed = await parseTaskFromAPI(userText, users);
    } catch {
      setLoading(false);
      setMessages(prev => [...prev, { id: `err-${Date.now()}`, role: 'assistant', text: 'Sorry, failed to parse the task. Please try again.' }]);
      return;
    }

    const user = users.find(u => u.name === parsed.assigneeName);
    const dueDateStr = parsed.dueDate
      ? new Date(parsed.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
      : 'No due date';

    const botMsg: ChatMessage = {
      id: `a-${Date.now()}`,
      role: 'assistant',
      text: `Got it! Here's what I'll create:`,
      parsedTask: parsed,
    };
    setMessages(prev => [...prev, botMsg]);
    setLoading(false);

    // Auto-create after showing preview
    setTimeout(() => {
      const column = parsed.status === 'backlog' ? 'Backlog' : 'To Do';
      onCreateTask({
        title: parsed.title,
        assigneeId: user?.id ?? currentUser.id,
        priority: parsed.priority,
        status: parsed.status,
        team: parsed.team,
        dueDate: parsed.dueDate,
        description: parsed.description,
        labels: parsed.labels,
      });
      const assigneeLabel = user?.name ?? currentUser.name;
      setMessages(prev => [
        ...prev,
        {
          id: `confirm-${Date.now()}`,
          role: 'assistant',
          text: `Task created and added to "${assigneeLabel}"'s ${column} column. Due: ${dueDateStr}.`,
        },
      ]);
    }, 600);
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(v => !v)}
        className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg transition-all duration-200 font-medium text-sm ${
          open ? 'bg-gray-700 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
      >
        {open ? <ChevronDown size={16} /> : <MessageSquarePlus size={16} />}
        {open ? 'Close' : 'AI Task'}
      </button>

      {/* Chat panel */}
      {open && (
        <div className='fixed bottom-16 sm:bottom-20 right-3 sm:right-6 z-40 w-[calc(100vw-1.5rem)] sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden max-h-[70vh] sm:max-h-none'>
          {/* Header */}
          <div className='flex items-center justify-between px-4 py-3 bg-blue-600 text-white'>
            <div className='flex items-center gap-2'>
              <div className='w-7 h-7 rounded-full bg-white/20 flex items-center justify-center'>
                <Sparkles size={14} />
              </div>
              <div>
                <p className='text-sm font-semibold'>AI Task Creator</p>
                <p className='text-[11px] text-blue-200'>Describe a task in plain English</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className='p-1 hover:bg-white/20 rounded-lg transition-colors'>
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div className='flex-1 overflow-y-auto px-4 py-4 space-y-3 max-h-[45vh] sm:max-h-80'>
            {messages.map(msg => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-2`}>
                {msg.role === 'assistant' && (
                  <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5'>
                    <Bot size={12} className='text-blue-600' />
                  </div>
                )}
                <div className={`max-w-[85%] space-y-2`}>
                  <div
                    className={`text-sm px-3 py-2 rounded-2xl leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white rounded-tr-sm'
                        : 'bg-gray-100 text-gray-800 rounded-tl-sm'
                    }`}
                  >
                    {msg.text}
                  </div>
                  {msg.parsedTask && (
                    <div className='bg-blue-50 border border-blue-100 rounded-xl px-3 py-2.5 space-y-1.5 text-xs'>
                      <p className='font-semibold text-blue-800 truncate'>{msg.parsedTask.title}</p>
                      <div className='flex flex-wrap gap-x-3 gap-y-1 text-blue-700'>
                        <span>👤 {msg.parsedTask.assigneeName || 'Unassigned'}</span>
                        <span>⚡ {msg.parsedTask.priority.charAt(0).toUpperCase() + msg.parsedTask.priority.slice(1)}</span>
                        {msg.parsedTask.dueDate && (
                          <span>📅 {new Date(msg.parsedTask.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className='flex gap-2 items-center'>
                <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center'>
                  <Bot size={12} className='text-blue-600' />
                </div>
                <div className='bg-gray-100 rounded-2xl rounded-tl-sm px-3 py-2.5 flex gap-1'>
                  {[0, 1, 2].map(i => (
                    <span key={i} className='w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce' style={{ animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className='border-t border-gray-100 px-3 py-3'>
            <div className='relative flex gap-2'>
              {mentionUsers.length > 0 && (
                <div className='absolute bottom-full left-0 right-10 mb-1 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden z-50'>
                  {mentionUsers.map(user => (
                    <button
                      key={user.id}
                      type='button'
                      onMouseDown={e => { e.preventDefault(); handleMentionSelect(user); }}
                      className='w-full flex items-center gap-2.5 px-3 py-2 hover:bg-blue-50 text-left transition-colors'
                    >
                      <div className={`w-6 h-6 rounded-full ${user.avatarColor} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}>
                        {user.initials}
                      </div>
                      <span className='text-sm text-gray-800'>{user.name}</span>
                    </button>
                  ))}
                </div>
              )}
              <input
                ref={chatInputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={e => {
                  if (e.key === 'Escape') { setMentionState(null); return; }
                  if (e.key === 'Enter' && !e.shiftKey) handleSend();
                }}
                placeholder='e.g. @Shreyas review vendor invoice by Monday...'
                className='flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:border-blue-500 placeholder-gray-400'
                disabled={loading}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || loading}
                className='px-3 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
              >
                <Send size={15} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
